import React from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const formatValue = (value, format) => {
  if (format === 'currency') return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  if (format === 'percent') return `${value}%`;
  return value.toLocaleString();
};

const configs = {
  blue: {
    gradient: 'from-blue-600 via-blue-500 to-indigo-600',
    glow: 'shadow-[0_12px_40px_rgba(59,130,246,0.38)]',
    hoverGlow: 'hover:shadow-[0_20px_55px_rgba(59,130,246,0.5)]',
    areaFill: '#93c5fd',
    areaStroke: '#dbeafe',
  },
  green: {
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    glow: 'shadow-[0_12px_40px_rgba(16,185,129,0.38)]',
    hoverGlow: 'hover:shadow-[0_20px_55px_rgba(16,185,129,0.5)]',
    areaFill: '#6ee7b7',
    areaStroke: '#d1fae5',
  },
  purple: {
    gradient: 'from-violet-600 via-purple-500 to-fuchsia-600',
    glow: 'shadow-[0_12px_40px_rgba(139,92,246,0.38)]',
    hoverGlow: 'hover:shadow-[0_20px_55px_rgba(139,92,246,0.5)]',
    areaFill: '#c4b5fd',
    areaStroke: '#ede9fe',
  },
  orange: {
    gradient: 'from-orange-500 via-amber-500 to-yellow-500',
    glow: 'shadow-[0_12px_40px_rgba(245,158,11,0.38)]',
    hoverGlow: 'hover:shadow-[0_20px_55px_rgba(245,158,11,0.5)]',
    areaFill: '#fcd34d',
    areaStroke: '#fef3c7',
  },
};

export default function HeroMetric({ title, value, sparklineData = [], format = 'number', color = 'blue', subtitle }) {
  const cfg = configs[color] || configs.blue;

  return (
    <div className={`relative rounded-2xl bg-gradient-to-br ${cfg.gradient} ${cfg.glow} ${cfg.hoverGlow} overflow-hidden cursor-default group transition-all duration-300 hover:-translate-y-1`}>
      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '18px 18px' }}
      />
      {/* Shine sweep on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{ background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.09) 50%, transparent 65%)' }}
      />

      {/* Live indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-50" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
        </span>
        <span className="text-white/40 text-[10px] font-bold uppercase tracking-widest">live</span>
      </div>

      <div className="relative pt-6 px-6 pb-0">
        <p className="text-white/60 text-[11px] font-bold uppercase tracking-[0.18em] mb-2">{title}</p>
        <p className="text-4xl font-black text-white tabular-nums leading-none tracking-tight">
          {formatValue(value, format)}
        </p>
        {subtitle && (
          <p className="text-white/55 text-xs font-medium mt-1.5">{subtitle}</p>
        )}
      </div>

      {sparklineData.length > 0 ? (
        <div className="h-14 mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparklineData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={cfg.areaFill} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={cfg.areaFill} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke={cfg.areaStroke} strokeWidth={2.5} fill={`url(#grad-${color})`} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-6" />
      )}
    </div>
  );
}
